import type { RouteObject } from 'react-router-dom'
import { createBrowserRouter } from 'react-router-dom'
import { AuthLayout } from '@/layouts/AuthLayout'
import { AuthSplitLayout } from '@/layouts/AuthSplitLayout'
import { MainShellLayout } from '@/layouts/MainShellLayout'
import { SurveyEntryLayout } from '@/layouts/SurveyEntryLayout'
import { NotFoundPage } from '@/shared/components/NotFoundPage'
import { RouteErrorBoundary } from '@/shared/components/RouteErrorBoundary'
import { HomePage } from '@/features/home/routes/HomePage'
import { AccountPage } from '@/features/account'
import { HistoryPage } from '@/features/history/routes/HistoryPage'
import { NotificationsPage } from '@/features/notifications/routes/NotificationsPage'
import { RewardsPage } from '@/features/rewards/routes/RewardsPage'
import { RewardViewRedirectPage } from '@/features/rewards/routes/RewardViewRedirectPage'
import { SelectBrandPage } from '@/features/rewards/routes/SelectBrandPage'
import {
  ForgotPasswordEmailSentPage,
  ForgotPasswordPage,
  ForceResetOtpPage,
  ForceResetPasswordPage,
  LoginPage,
  ResetPasswordPage,
  ResetPasswordSuccessPage,
  SetPasswordPage,
  SignupVerifyPage,
  VerifyOtpPage,
} from '@/features/auth'
import {
  InstrumentInfoPage,
  InstrumentLinkOnboardingPage,
  InstrumentPathEntryPage,
  SignupSurveySharePage,
  SurveyExecutionPage,
  WebSurveyAccountPage,
} from '@/features/instrument-onboarding'
import { DeleteAccountPage } from '@/features/account-deletion'
import { RequireAuth } from './guards/RequireAuth'
import { RequireGuest } from './guards/RequireGuest'
export const routes: RouteObject[] = [
  {
    path: '/brushfire/delete-account',
    element: <DeleteAccountPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/s/:slug',
    element: <SignupSurveySharePage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/instruments/:instrumentId',
    element: <InstrumentPathEntryPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/instrument/:instrumentId',
    element: <InstrumentPathEntryPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AuthLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: '/instrument/onboarding/account',
            element: <WebSurveyAccountPage />,
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
      {
        element: <AuthSplitLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: '/set-password',
            element: <SetPasswordPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/force-reset/password',
            element: <ForceResetPasswordPage />,
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
      {
        path: '/',
        element: <MainShellLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            element: <HomePage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'history',
            element: <HistoryPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'notifications',
            element: <NotificationsPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'account',
            element: <AccountPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'rewards',
            element: <RewardsPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'rewards/claimed/:transactionId',
            element: <RewardsPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'rewards/select-brand',
            element: <SelectBrandPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'rewards/view/:transactionId',
            element: <RewardViewRedirectPage />,
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
      {
        element: <SurveyEntryLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: 'instrument/:instrumentId/info',
            element: <InstrumentInfoPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'instrument/:instrumentId/survey',
            element: <SurveyExecutionPage />,
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
    ],
  },
  {
    element: <AuthLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/instrument/onboarding',
        element: <InstrumentLinkOnboardingPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        element: <RequireGuest />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: '/force-reset/otp',
            element: <ForceResetOtpPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/forgot-password',
            element: <ForgotPasswordPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/forgot-password/sent',
            element: <ForgotPasswordEmailSentPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/verify-otp',
            element: <VerifyOtpPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/reset-password/success',
            element: <ResetPasswordSuccessPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/reset-password',
            element: <ResetPasswordPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/reset-password/:token',
            element: <ResetPasswordPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/signup/verify',
            element: <SignupVerifyPage />,
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
    ],
  },
  {
    element: <RequireGuest />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AuthSplitLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: '/login',
            element: <LoginPage />,
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage homeTo="/" />,
    errorElement: <RouteErrorBoundary />,
  },
]

export const router = createBrowserRouter(routes)
